<script setup lang="ts">
import { ElForm } from 'element-plus'

/**
 * The form shell: labels, spacing and the `@submit` that runs the submit pipeline. Fields
 * go in `FormItem`s, and validation is zod through `useEntityForm` — **not** element-plus
 * `rules`, which nothing here drives.
 *
 * `compact` is for a filter panel or a dense block. `locked` is for a record that may not
 * be edited at all; `disabled` is refused by ESLint on this component, because it reads as
 * "a request is in flight" and takes Save down with it.
 */
defineProps<{
  labelPosition?: 'left' | 'right' | 'top'
  labelWidth?: string | number
  size?: 'default' | 'large' | 'small'
  disabled?: boolean
  locked?: boolean
  compact?: boolean
}>()

const emit = defineEmits<{
  (e: 'submit', event: Event): void
}>()
</script>

<template>
  <ElForm
    class="shared-form"
    :class="{ 'shared-form--compact': compact }"
    :label-position="labelPosition ?? 'top'"
    :label-width="labelWidth"
    :size="size"
    :disabled="disabled || locked"
    v-bind="$attrs"
    @submit.prevent="emit('submit', $event)"
  >
    <slot />
  </ElForm>
</template>

<style lang="scss" scoped>
.shared-form :deep(.el-form-item__content) {
  line-height: 40px;
}

.shared-form--compact :deep(.el-form-item) {
  margin-bottom: 8px;
}

.shared-form--compact :deep(.el-form-item__label) {
  margin-bottom: 2px;
  line-height: 1.2;
}
</style>
